import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import { getSessionId } from '../lib/supabase';
import { getLeadAuftragsnummer } from '../services/leadsService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SecurityBadges from '../components/SecurityBadges';

const CopyIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-4 h-4 ${className}`}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-4 h-4 ${className}`}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-2 text-gray-400 hover:text-prussian-blue transition-colors inline-flex items-center"
      title="Kopieren"
    >
      {copied ? <CheckIcon className="text-green-600" /> : <CopyIcon />}
    </button>
  );
};

const BankTransfer: React.FC = () => {
  const navigate = useNavigate();
  const { formData } = useFormContext();
  const [auftragsnummer, setAuftragsnummer] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Redirect to payment if no order number available
  useEffect(() => {
    if (!formData.orderNumber) {
      navigate('/payment');
    }
  }, [formData.orderNumber, navigate]);

  // Fetch auftragsnummer from leads table
  useEffect(() => {
    const fetchAuftragsnummer = async () => {
      try {
        const sessionId = await getSessionId();
        if (sessionId) {
          const result = await getLeadAuftragsnummer(sessionId);
          setAuftragsnummer(result);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (formData.orderNumber) {
      fetchAuftragsnummer();
    }
  }, [formData.orderNumber]);

  if (!formData.orderNumber || isLoading) {
    return null;
  }

  const displayNumber = auftragsnummer || formData.orderNumber;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-5xl">
        <div className="form-container fade-in">
          <h2 className="text-2xl md:text-3xl text-center font-bold text-prussian-blue mb-6">
            Vielen Dank für Ihren Auftrag!<br />Bitte nehmen Sie nun die Banküberweisung vor.
          </h2>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 md:p-8 mb-6">
            <p className="text-base md:text-lg text-gray-900">
              Ihre Bestellung wurde erfolgreich erstellt.
            </p>
            <p className="text-base md:text-lg text-gray-900 mt-1">
              Auftragsnummer: <strong className="text-prussian-blue">#{displayNumber}</strong>
            </p>
            <p className="text-base md:text-lg text-gray-900 mt-1">
              Status: <strong className="text-prussian-blue">Zahlung ausstehend</strong>
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 md:p-8 mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-prussian-blue mb-5">
              Bitte überweisen Sie den Rechnungsbetrag auf das folgende Bankkonto:
            </h3>

            <div className="space-y-2.5 text-base md:text-lg text-gray-900">
              {[
                { label: 'Kontoinhaber', value: 'YN Digital GmbH', copy: 'YN Digital GmbH' },
                { label: 'IBAN', value: 'DE40 1001 8000 0877 8600 96', copy: 'DE40100180000877860096' },
                { label: 'BIC', value: 'SOBKDEB2', copy: 'SOBKDEB2' },
                { label: 'Bank', value: 'Finom Bank Zweigniederlassung Deutschland' },
                { label: 'Verwendungszweck', value: `Auftrag ${displayNumber}`, copy: `Auftrag ${displayNumber}`, bold: true },
                { label: 'Betrag', value: '34,80 EUR (inkl. MwSt.)', bold: true },
              ].map(({ label, value, copy, bold }) => (
                <div key={label}>
                  <span className="font-semibold">{label}: </span>
                  <span className={bold ? 'font-bold text-prussian-blue' : ''}>
                    {value}
                  </span>
                  {copy && <CopyButton text={copy} />}
                </div>
              ))}
            </div>

            <p className="text-base text-gray-700 mt-5 pt-4 border-t border-gray-200">
              <strong>Wichtig:</strong> Bitte geben Sie unbedingt Ihre Auftragsnummer im Verwendungszweck an, damit wir Ihre Zahlung schnell und korrekt zuordnen können.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 md:p-8 mb-6">
            <p className="text-base md:text-lg text-gray-900 mb-3">
              Nachdem wir Ihre Zahlung erhalten haben, werden wir Ihre Melderegisterauskunft umgehend bearbeiten. Sie erhalten das Ergebnis per E-Mail.
            </p>
            <p className="text-base md:text-lg text-gray-700">
              <strong>Bitte beachten Sie:</strong> Ihre Anfrage wird erst bearbeitet, wenn die Zahlung bei uns eingegangen ist.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 md:p-8 mb-6">
            <p className="text-base md:text-lg text-gray-900">
              Bei Fragen stehen wir Ihnen gerne zur Verfügung. Sie erreichen uns jederzeit unter{' '}
              <a href="mailto:info@adressfinder.org" className="text-prussian-blue font-medium">
                info@adressfinder.org
              </a>.
            </p>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/payment')}
              className="inline-block bg-prussian-blue hover:bg-opacity-90 text-white font-medium py-3 px-8 rounded-md transition duration-200 text-base md:text-lg"
            >
              Mit anderer Zahlungsart bezahlen
            </button>
          </div>
        </div>

        <div className="mt-8">
          <SecurityBadges />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BankTransfer;
